import { useCallback, useEffect, useMemo, useRef } from "react";
import { FuseHit, StringIndexedObject } from "@kanda-libs/ks-design-library";
import type {
  TableHeaderColumn,
  TableProps,
} from "@kanda-libs/ks-component-ts";
import type { Job } from "@kanda-libs/ks-frontend-services";
import { formatJobs, sortData } from "components/job/JobTable/helpers";
import type {
  FormattedTableJob,
  JobWithCompanyAndCredit,
} from "components/job/JobTable/types";
import usePagination from "hooks/usePagination";
import {
  TABLE_COLUMNS,
  PAYOUT_PENDING_COLUMNS,
  FLAG_TABLE_COLUMN,
} from "components/job/JobTable/constants";
import useJobTableSorting from "components/job/JobTable/useJobTableSorting";
import { useHistory } from "react-router-dom";
import { URLS } from "common/constants";
import { useSelector } from "react-redux";
import companySelectors from "store/selectors/company";

export interface JobTablePropsHook extends TableProps {
  unpaginatedData: FormattedTableJob[];
}

export default function useJobTableProps(
  solar = false,
  satNoteSigned = false,
  pendingPayout = false,
  hits: FuseHit<Job>[] = [],
  query = "",
  isLoading = false,
  showHeader = false
): JobTablePropsHook {
  const { push } = useHistory();
  const { sorting, order, onAction } = useJobTableSorting();

  const { pageIndex, perPage, totalPages, setPageIndex } = usePagination(
    hits.length
  );

  const companies = useSelector(companySelectors.getEntitiesAsArray);

  const defaultColumns = showHeader
    ? [FLAG_TABLE_COLUMN, ...TABLE_COLUMNS]
    : TABLE_COLUMNS;

  const columns = useMemo(
    () =>
      pendingPayout
        ? [...PAYOUT_PENDING_COLUMNS, ...defaultColumns]
        : defaultColumns,
    [pendingPayout]
  );

  const data = useMemo(() => {
    const rawData = hits.map((hit) => hit.item);
    return sortData(rawData, sorting, order);
  }, [order, sorting, hits]);

  // Data slicing is used in place of API pagination - in real use, use API
  // pagination
  const paginatedData = useMemo(() => {
    return (
      data?.slice(pageIndex * perPage, pageIndex * perPage + perPage) || []
    );
  }, [data, pageIndex, perPage]);

  const setPage = useCallback(
    (index: number) => setPageIndex(index),
    [setPageIndex]
  );

  useEffect(() => {
    setPage(0);
  }, [solar, satNoteSigned, pendingPayout, setPage]);

  const queryRef = useRef("");

  // Effect resets page index to 0 when query changes - when user searches
  useEffect(() => {
    if (queryRef.current && queryRef.current === query) return;
    setPageIndex(0);
  }, [query, setPageIndex]);

  const onRowClicked = useCallback(
    (row?: StringIndexedObject) => {
      if (row) {
        const url = URLS.JOB.replace("{id}", row.original.id);
        push(url);
      }
    },
    [push]
  );

  const formattedData = useMemo(
    () => formatJobs(paginatedData, companies),
    [paginatedData, companies]
  );

  const formattedUnpaginatedData = useMemo(
    () => formatJobs(data as JobWithCompanyAndCredit[], companies),
    [data, companies]
  );

  return {
    data: formattedData,
    onRowClicked,
    onAction,
    pageIndex,
    totalPages,
    setPage,
    columns: columns as TableHeaderColumn[],
    unpaginatedData: formattedUnpaginatedData,
    isLoading,
  };
}
